import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Save, Sparkles, ChevronDown, ChevronUp, CheckCircle2, XCircle,
  Edit3, Loader2, AlertTriangle, Info
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const CONFIDENCE_STYLE = {
  high: 'bg-green-50 text-green-700 border-green-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-500 border-gray-200',
};

function mergeContent(existing) {
  const defaults = {
    educational_implications: '',
    potential_eligibility: { statement: '', categories: [] },
    _sources: {},
  };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.educational_implications) {
    return { ...defaults, educational_implications: existing.notes };
  }
  return {
    ...defaults, ...existing,
    potential_eligibility: { ...defaults.potential_eligibility, ...(existing.potential_eligibility || {}) },
  };
}

export default function EducationalImplicationsEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [aiOpen, setAiOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [draftStates, setDraftStates] = useState({});

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  const handleSave = () => {
    const { _sources, ...saveData } = content;
    if (_sources && Object.keys(_sources).length > 0) saveData._sources = _sources;
    onSave(saveData);
  };

  const generateDraft = async () => {
    setGenerating(true);
    setDraftData(null);
    setAiMeta(null);
    setDraftStates({});
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/met-sections/met_educational_implications/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output) {
        setDraftData(output);
        setAiMeta({
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          gaps: output.gaps_identified || [],
          model: res.data?.model_used,
          inputSnapshot: res.data?.input_snapshot,
        });
        const initial = {};
        if (output.educational_implications_draft) initial.implications = 'pending';
        if (output.potential_eligibility_statement) initial.eligibility = 'pending';
        setDraftStates(initial);
      }
    } catch (err) {
      console.error('AI drafting failed', err);
      setAiMeta({ error: err.response?.data?.detail || 'AI drafting failed. Please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const acceptDraft = (field) => {
    if (field === 'implications') {
      setContent(prev => ({
        ...prev,
        educational_implications: draftData.educational_implications_draft,
        _sources: { ...(prev._sources || {}), educational_implications: 'ai_suggested' },
      }));
    } else if (field === 'eligibility') {
      setContent(prev => ({
        ...prev,
        potential_eligibility: {
          statement: draftData.potential_eligibility_statement,
          categories: draftData.potential_categories || [],
        },
        _sources: { ...(prev._sources || {}), potential_eligibility: 'ai_suggested' },
      }));
    }
    setDraftStates(prev => ({ ...prev, [field]: 'accepted' }));
  };

  const editDraft = (field) => {
    if (field === 'implications') {
      setContent(prev => ({
        ...prev,
        educational_implications: draftData.educational_implications_draft,
        _sources: { ...(prev._sources || {}), educational_implications: 'ai_edited' },
      }));
    } else if (field === 'eligibility') {
      setContent(prev => ({
        ...prev,
        potential_eligibility: {
          statement: draftData.potential_eligibility_statement,
          categories: draftData.potential_categories || [],
        },
        _sources: { ...(prev._sources || {}), potential_eligibility: 'ai_edited' },
      }));
    }
    setDraftStates(prev => ({ ...prev, [field]: 'edited' }));
  };

  const rejectDraft = (field) => {
    setDraftStates(prev => ({ ...prev, [field]: 'rejected' }));
  };

  const pendingCount = Object.values(draftStates).filter(s => s === 'pending').length;
  const acceptAllPending = () => {
    Object.entries(draftStates).forEach(([k, v]) => {
      if (v === 'pending') acceptDraft(k);
    });
  };

  const source = content._sources || {};

  return (
    <div className="max-w-4xl space-y-6" data-testid="ed-implications-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="ed-implications-title">Educational Implications</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="ed-implications-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI Drafting Panel */}
      <div className="bg-gradient-to-r from-[#5A8A5A]/5 to-[#3B6A9B]/5 border border-[#5A8A5A]/20 rounded-2xl overflow-hidden" data-testid="ai-ed-implications-panel">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setAiOpen(!aiOpen)} data-testid="ed-implications-ai-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#5A8A5A]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI Draft — Educational Implications</span>
            <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">Advisory Only</Badge>
          </div>
          {aiOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {aiOpen && (
          <div className="px-5 pb-5 border-t border-[#5A8A5A]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI drafts educational implications based on evaluation data, interpretation, and summary findings. Review, edit, and finalize before accepting.
              </p>
              <Button onClick={generateDraft} disabled={generating}
                className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl text-xs h-8 shrink-0" data-testid="generate-ed-implications-btn">
                {generating ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Drafting...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{draftData ? 'Regenerate' : 'Generate Draft'}</>}
              </Button>
            </div>

            {aiMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="ed-implications-ai-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{aiMeta.error}</p>
              </div>
            )}

            {draftData && (
              <>
                {aiMeta && !aiMeta.error && (
                  <div className="bg-white/60 border border-[#E5E5E5] rounded-xl p-3 mb-4" data-testid="ed-implications-ai-meta">
                    <div className="flex items-start gap-4 text-xs text-[#595959]">
                      <div className="flex-1">
                        {aiMeta.notes && <p className="mb-1"><strong>AI Notes:</strong> {aiMeta.notes}</p>}
                        {aiMeta.dataSources?.length > 0 && <p className="mb-1"><strong>Data used:</strong> {aiMeta.dataSources.join(', ')}</p>}
                        {aiMeta.gaps?.length > 0 && <p className="text-amber-600"><strong>Data gaps:</strong> {aiMeta.gaps.join('; ')}</p>}
                      </div>
                      {pendingCount > 0 && (
                        <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 border-green-300 text-green-700 hover:bg-green-50 shrink-0" onClick={acceptAllPending} data-testid="accept-all-ed-implications-btn">
                          <CheckCircle2 className="h-3 w-3 mr-1" />Accept All
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Educational Implications Draft */}
                {draftData.educational_implications_draft && (
                  <DraftCard
                    label="Educational Implications Draft"
                    text={draftData.educational_implications_draft}
                    state={draftStates.implications}
                    onAccept={() => acceptDraft('implications')}
                    onEdit={() => editDraft('implications')}
                    onReject={() => rejectDraft('implications')}
                    testIdPrefix="draft-implications"
                  />
                )}

                {/* Evidence Basis */}
                {draftData.evidence_basis?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-3 mt-3" data-testid="evidence-basis">
                    <h4 className="text-xs font-bold text-[#595959] mb-2">Evidence Basis</h4>
                    {draftData.evidence_basis.map((e, i) => (
                      <div key={i} className="border-l-2 border-[#5A8A5A]/30 pl-3 py-1 mb-1.5">
                        <p className="text-xs text-[#1C1C1C]">{e.implication}</p>
                        <p className="text-[10px] text-[#595959] italic">{e.supporting_data}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Potential Eligibility Draft */}
                {draftData.potential_eligibility_statement && (
                  <div className="mt-3">
                    <DraftCard
                      label="Potential Eligibility Statement (Advisory)"
                      text={draftData.potential_eligibility_statement}
                      state={draftStates.eligibility}
                      onAccept={() => acceptDraft('eligibility')}
                      onEdit={() => editDraft('eligibility')}
                      onReject={() => rejectDraft('eligibility')}
                      testIdPrefix="draft-eligibility"
                      categories={draftData.potential_categories}
                    />
                  </div>
                )}

                {aiMeta?.inputSnapshot && (
                  <div className="mt-3 text-[10px] text-[#595959]/60 flex items-center gap-2">
                    <Info className="h-3 w-3" />
                    <span>Sources: {aiMeta.inputSnapshot.data_sources?.join(', ') || 'none'}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Manual editing areas */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="ed-implications-field">
        <div className="flex items-center gap-2 mb-2">
          <label className="text-sm font-medium text-[#1C1C1C]">Documentation of Educational Implications of Suspected Disability</label>
          {source.educational_implications === 'ai_suggested' && <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A]">AI</Badge>}
          {source.educational_implications === 'ai_edited' && <Badge className="text-[10px] bg-blue-50 text-blue-600">AI Edited</Badge>}
        </div>
        <p className="text-xs text-[#595959] mb-3">Document how the suspected disability impacts the student's educational performance.</p>
        <Textarea
          value={content.educational_implications || ''}
          onChange={e => setContent(prev => ({ ...prev, educational_implications: e.target.value }))}
          placeholder="Document how the suspected disability impacts educational performance..."
          rows={8}
          className="rounded-xl border-[#E5E5E5] text-sm"
          data-testid="ed-implications-input"
        />
      </div>

      <div className="bg-white border border-amber-200 rounded-2xl p-5" data-testid="potential-eligibility-field">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <label className="text-sm font-medium text-[#1C1C1C]">Statement of Potential Eligibility</label>
          {source.potential_eligibility === 'ai_suggested' && <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A]">AI</Badge>}
          {source.potential_eligibility === 'ai_edited' && <Badge className="text-[10px] bg-blue-50 text-blue-600">AI Edited</Badge>}
        </div>
        <p className="text-xs text-amber-700 mb-3 italic">Advisory only — final determination is made by the evaluation team.</p>
        <Textarea
          value={content.potential_eligibility.statement || ''}
          onChange={e => setContent(prev => ({
            ...prev, potential_eligibility: { ...prev.potential_eligibility, statement: e.target.value },
          }))}
          placeholder="Provide a statement regarding potential eligibility..."
          rows={4}
          className="rounded-xl border-[#E5E5E5] text-sm"
          data-testid="potential-eligibility-input"
        />
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="ed-implications-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Educational Implications'}
        </Button>
      </div>
    </div>
  );
}

function DraftCard({ label, text, state, onAccept, onEdit, onReject, testIdPrefix, categories }) {
  const isPending = state === 'pending';
  return (
    <div className={`border rounded-xl overflow-hidden ${
      state === 'accepted' ? 'bg-green-50/30 border-green-200' :
      state === 'edited' ? 'bg-blue-50/30 border-blue-200' :
      state === 'rejected' ? 'bg-gray-50 border-gray-200 opacity-50' :
      'border-[#E5E5E5]'
    }`} data-testid={testIdPrefix}>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold text-[#5A8A5A] uppercase tracking-wider">{label}</span>
          {state && state !== 'pending' && (
            <Badge className={`text-[10px] ${
              state === 'accepted' ? 'bg-green-100 text-green-700' :
              state === 'edited' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>{state === 'accepted' ? 'Accepted' : state === 'edited' ? 'Accepted (to edit)' : 'Rejected'}</Badge>
          )}
        </div>
        <p className="text-sm text-[#1C1C1C] whitespace-pre-wrap leading-relaxed mb-2">{text}</p>
        {categories?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {categories.map((c, i) => (
              <Badge key={i} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">{c}</Badge>
            ))}
          </div>
        )}
        {isPending && (
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[#E5E5E5]">
            <Button variant="outline" size="sm" onClick={onAccept} className="h-7 rounded-lg text-xs text-green-700 border-green-300 hover:bg-green-50" data-testid={`${testIdPrefix}-accept`}>
              <CheckCircle2 className="h-3 w-3 mr-1" />Accept
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} className="h-7 rounded-lg text-xs text-blue-600 border-blue-300 hover:bg-blue-50" data-testid={`${testIdPrefix}-edit`}>
              <Edit3 className="h-3 w-3 mr-1" />Accept & Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onReject} className="h-7 rounded-lg text-xs text-red-400 border-red-200 hover:bg-red-50" data-testid={`${testIdPrefix}-reject`}>
              <XCircle className="h-3 w-3 mr-1" />Reject
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
